/**
 * Frozen delivery postcode matcher (DPD Highlands & Islands / offshore).
 * Matches on UK outward district codes with numeric range rules — not full-string equality.
 *
 * Built-in rules: DPD Two Day / 2–4 day bands (Tier B + C). Theme extras are additive.
 */
(function (root) {
  'use strict';

  var CART_ATTRIBUTE_KEY = 'frozen_delivery_postcode';
  var STORAGE_KEY = 'frozen_delivery_postcode';

  /**
   * @param {string} postcode
   * @returns {string}
   */
  function normalizePostcode(postcode) {
    if (!postcode) return '';
    return String(postcode)
      .replace(/[\s\u00A0\u2007\u202F]+/g, '')
      .toUpperCase()
      .trim();
  }

  /**
   * Full UK postcode format (outward + inward required).
   * Outward-only values like "IV63" are not accepted for storage.
   * @param {string} postcode
   * @returns {boolean}
   */
  function isValidUkPostcodeFormat(postcode) {
    var full = normalizePostcode(postcode);
    if (!full) return false;
    // AA9A 9AA / A9 9AA / A99 9AA / AA9 9AA / AA99 9AA
    return /^[A-Z]{1,2}\d[A-Z\d]?\d[A-Z]{2}$/.test(full);
  }

  /**
   * Format a normalised postcode with a space before the inward code.
   * @param {string} postcode
   * @returns {string}
   */
  function formatUkPostcode(postcode) {
    var full = normalizePostcode(postcode);
    if (!isValidUkPostcodeFormat(full)) return full;
    return full.slice(0, -3) + ' ' + full.slice(-3);
  }

  /**
   * Confirm the postcode exists via postcodes.io (distinguishes typos from real codes).
   * @param {string} postcode
   * @returns {Promise<{ ok: boolean, reason: string|null, result: object|null }>}
   */
  function validatePostcodeExists(postcode) {
    var full = normalizePostcode(postcode);
    if (!isValidUkPostcodeFormat(full)) {
      return Promise.resolve({ ok: false, reason: 'invalid_format', result: null });
    }

    var url = 'https://api.postcodes.io/postcodes/' + encodeURIComponent(full) + '/validate';
    return fetch(url, { method: 'GET', headers: { Accept: 'application/json' } })
      .then(function (res) {
        return res.json().then(function (body) {
          if (!res.ok) {
            return { ok: false, reason: 'lookup_failed', result: null };
          }
          // { status: 200, result: true|false }
          if (body && body.result === true) {
            return { ok: true, reason: null, result: body };
          }
          return { ok: false, reason: 'not_found', result: body };
        });
      })
      .catch(function () {
        return { ok: false, reason: 'lookup_failed', result: null };
      });
  }

  /**
   * Parse a UK postcode into area + district number (+ optional letter suffix).
   * Accepts full postcodes or outward-only (e.g. IV63 6AB, IV636AB, IV63, SW1A1AA).
   *
   * @param {string} postcode
   * @returns {{ area: string, districtNum: number, districtSuffix: string, outward: string, full: string, sector: string|null }|null}
   */
  function parseUkPostcode(postcode) {
    var full = normalizePostcode(postcode);
    if (!full) return null;

    // Outward + optional inward: area(1–2) + district digits(1–2) + optional letter + optional inward
    var match = full.match(/^([A-Z]{1,2})(\d{1,2})([A-Z]?)(?:(\d)([A-Z]{2}))?$/);
    if (!match) return null;

    var area = match[1];
    var districtDigits = match[2];
    var districtSuffix = match[3] || '';
    var districtNum = parseInt(districtDigits, 10);
    if (isNaN(districtNum)) return null;

    var outward = area + districtDigits + districtSuffix;
    var sector = match[4] ? outward + ' ' + match[4] : null;

    return {
      area: area,
      districtNum: districtNum,
      districtSuffix: districtSuffix,
      outward: outward,
      full: full,
      sector: sector
    };
  }

  /**
   * Built-in DPD frozen exclusions (Tier B Two Day + Tier C 2–4 days + non-GB).
   * Isle of Wight (PO30–41) included pending client confirmation (Tier D).
   *
   * @param {{ area: string, districtNum: number, districtSuffix: string, outward: string }} parsed
   * @returns {{ restricted: boolean, tier: string|null, reason: string|null }}
   */
  function matchBuiltinRestriction(parsed) {
    var area = parsed.area;
    var n = parsed.districtNum;
    var outward = parsed.outward;

    // Special / non-geographic — do not treat as mainland next-day
    if (outward === 'KY99' || outward === 'G90' || outward === 'GIR0AA' || area === 'BX' || area === 'XX') {
      return { restricted: true, tier: 'special', reason: 'non_geographic' };
    }

    // Whole-area offshore / NI / Crown Dependencies / Hebrides / Shetland
    if (area === 'BT' || area === 'IM' || area === 'GY' || area === 'JE' || area === 'HS' || area === 'ZE') {
      return { restricted: true, tier: area === 'HS' || area === 'ZE' ? 'C' : 'B', reason: 'area' };
    }

    // Scilly
    if (area === 'TR' && n >= 21 && n <= 25) {
      return { restricted: true, tier: 'B', reason: 'TR21-25' };
    }

    // Isle of Wight (Tier D — commercially unresolved; exclude until confirmed OK)
    if (area === 'PO' && n >= 30 && n <= 41) {
      return { restricted: true, tier: 'D', reason: 'PO30-41' };
    }

    // Aberdeen remote
    if (area === 'AB') {
      if ((n >= 31 && n <= 39) || (n >= 41 && n <= 45) || (n >= 51 && n <= 56)) {
        return { restricted: true, tier: 'B', reason: 'AB_remote' };
      }
    }

    // Stirlingshire remote
    if (area === 'FK' && n >= 17 && n <= 21) {
      return { restricted: true, tier: 'B', reason: 'FK17-21' };
    }

    // All IV is H&I on DPD (Inverness city included)
    if (area === 'IV') {
      return { restricted: true, tier: 'B', reason: 'IV' };
    }

    // Arran / Cumbrae
    if (area === 'KA' && (n === 27 || n === 28)) {
      return { restricted: true, tier: 'B', reason: 'KA27-28' };
    }

    // All KW (Caithness + Orkney)
    if (area === 'KW') {
      return { restricted: true, tier: n >= 15 && n <= 17 ? 'C' : 'B', reason: 'KW' };
    }

    // Argyll / islands (PA20+)
    if (area === 'PA') {
      if (
        (n >= 20 && n <= 38) ||
        (n >= 41 && n <= 49) ||
        (n >= 60 && n <= 78) ||
        n === 80
      ) {
        return { restricted: true, tier: 'B', reason: 'PA_remote' };
      }
    }

    // Perthshire remote + islands
    if (area === 'PH') {
      if (
        (n >= 15 && n <= 26) ||
        (n >= 30 && n <= 44) ||
        n === 49 ||
        n === 50
      ) {
        var tier = n >= 42 && n <= 44 ? 'C' : 'B';
        return { restricted: true, tier: tier, reason: 'PH_remote' };
      }
    }

    return { restricted: false, tier: null, reason: null };
  }

  /**
   * Expand a theme-setting entry into outward district codes.
   * Supports AB31-AB35, KA27, IV63.
   *
   * @param {string} rangeStr
   * @returns {string[]}
   */
  function expandDistrictRange(rangeStr) {
    if (!rangeStr) return [];
    var cleaned = normalizePostcode(rangeStr);

    // Whole-area token (e.g. BT, IM) — only two-letter areas to avoid G matching GY
    if (/^[A-Z]{2}$/.test(cleaned)) {
      return [cleaned];
    }

    if (!String(rangeStr).includes('-')) {
      var single = parseUkPostcode(rangeStr);
      return single ? [single.outward] : [cleaned].filter(Boolean);
    }

    var normalized = normalizePostcode(rangeStr);
    var parts = normalized.split('-');
    if (parts.length !== 2) {
      var fallback = parseUkPostcode(rangeStr);
      return fallback ? [fallback.outward] : [];
    }

    var start = parseUkPostcode(parts[0]);
    var end = parseUkPostcode(parts[1]);
    if (!start || !end || start.area !== end.area) {
      return start ? [start.outward] : [];
    }

    // Letter-suffixed districts (e.g. SW1A-SW1D) — rare for H&I; expand by letter
    if (start.districtSuffix && end.districtSuffix && start.districtNum === end.districtNum) {
      var out = [];
      for (var c = start.districtSuffix.charCodeAt(0); c <= end.districtSuffix.charCodeAt(0); c++) {
        out.push(start.area + String(start.districtNum) + String.fromCharCode(c));
      }
      return out;
    }

    if (start.districtNum > end.districtNum) return [start.outward];

    var districts = [];
    for (var num = start.districtNum; num <= end.districtNum; num++) {
      districts.push(start.area + String(num));
    }
    return districts;
  }

  /**
   * @param {string} postcodesStr
   * @returns {string[]}
   */
  function expandExtraDistricts(postcodesStr) {
    if (!postcodesStr) return [];
    var entries = String(postcodesStr)
      .split(/[,\n]/)
      .map(function (pc) {
        return pc.trim();
      })
      .filter(Boolean);

    var expanded = [];
    entries.forEach(function (entry) {
      expanded.push.apply(expanded, expandDistrictRange(entry));
    });
    return Array.from(new Set(expanded));
  }

  /**
   * @param {string} postcode
   * @param {string} [extraPostcodesStr] Theme setting extras (additive)
   * @returns {{
   *   valid: boolean,
   *   restricted: boolean,
   *   frozenOk: boolean,
   *   tier: string|null,
   *   reason: string|null,
   *   outward: string|null,
   *   full: string|null
   * }}
   */
  function evaluateFrozenDelivery(postcode, extraPostcodesStr) {
    var normalized = normalizePostcode(postcode);
    if (!normalized) {
      return {
        valid: false,
        restricted: true,
        frozenOk: false,
        tier: null,
        reason: 'invalid_postcode',
        outward: null,
        full: null
      };
    }

    // Require a full postcode (with inward code) before district matching / storage
    if (!isValidUkPostcodeFormat(normalized)) {
      return {
        valid: false,
        restricted: true,
        frozenOk: false,
        tier: null,
        reason: 'invalid_format',
        outward: null,
        full: normalized
      };
    }

    var parsed = parseUkPostcode(normalized);
    if (!parsed) {
      return {
        valid: false,
        restricted: true,
        frozenOk: false,
        tier: null,
        reason: 'invalid_postcode',
        outward: null,
        full: normalized
      };
    }

    var builtin = matchBuiltinRestriction(parsed);
    if (builtin.restricted) {
      return {
        valid: true,
        restricted: true,
        frozenOk: false,
        tier: builtin.tier,
        reason: builtin.reason,
        outward: parsed.outward,
        full: parsed.full
      };
    }

    var extras = expandExtraDistricts(extraPostcodesStr || '');
    var hitExtra = extras.some(function (token) {
      if (token === parsed.outward) return true;
      if (/^[A-Z]{2}$/.test(token) && parsed.area === token) return true;
      return false;
    });
    if (hitExtra) {
      return {
        valid: true,
        restricted: true,
        frozenOk: false,
        tier: 'extra',
        reason: 'theme_extra',
        outward: parsed.outward,
        full: parsed.full
      };
    }

    return {
      valid: true,
      restricted: false,
      frozenOk: true,
      tier: 'A',
      reason: null,
      outward: parsed.outward,
      full: parsed.full
    };
  }

  /**
   * @param {string} postcode
   * @param {string} [extraPostcodesStr]
   * @returns {boolean}
   */
  function isPostcodeUndeliverableForFrozen(postcode, extraPostcodesStr) {
    return evaluateFrozenDelivery(postcode, extraPostcodesStr).restricted;
  }

  /**
   * Persist postcode to cart attributes (survives to checkout / order).
   * @param {string|null} postcode
   * @returns {Promise<object|null>}
   */
  function syncCartPostcodeAttribute(postcode) {
    var routes = (root.theme && root.theme.routes) || {};
    var updateUrl = routes.cartUpdate || '/cart/update.js';
    var value = postcode ? normalizePostcode(postcode) : '';
    var attrs = {};
    attrs[CART_ATTRIBUTE_KEY] = value;
    attrs.delivery_postcode = value;

    return fetch(updateUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ attributes: attrs })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('cart attribute update failed');
        return res.json();
      })
      .catch(function (err) {
        console.error('[FrozenPostcode] Failed to sync cart attribute:', err);
        return null;
      });
  }

  var api = {
    STORAGE_KEY: STORAGE_KEY,
    CART_ATTRIBUTE_KEY: CART_ATTRIBUTE_KEY,
    normalizePostcode: normalizePostcode,
    formatUkPostcode: formatUkPostcode,
    isValidUkPostcodeFormat: isValidUkPostcodeFormat,
    validatePostcodeExists: validatePostcodeExists,
    parseUkPostcode: parseUkPostcode,
    matchBuiltinRestriction: matchBuiltinRestriction,
    expandDistrictRange: expandDistrictRange,
    expandExtraDistricts: expandExtraDistricts,
    evaluateFrozenDelivery: evaluateFrozenDelivery,
    isPostcodeUndeliverableForFrozen: isPostcodeUndeliverableForFrozen,
    syncCartPostcodeAttribute: syncCartPostcodeAttribute,
    // Back-compat aliases used by older theme code
    expandPostcodeRange: expandDistrictRange,
    expandAndNormalizePostcodes: expandExtraDistricts
  };

  root.FrozenPostcode = api;
  root.normalizePostcode = normalizePostcode;
  root.expandPostcodeRange = expandDistrictRange;
  root.expandAndNormalizePostcodes = expandExtraDistricts;
  root.isPostcodeUndeliverableForFrozen = isPostcodeUndeliverableForFrozen;
  root.evaluateFrozenDelivery = evaluateFrozenDelivery;
})(typeof window !== 'undefined' ? window : globalThis);
